<template>
  <div class="select-payment-gateway">
    <div
      class="payment-gateway-option multiple-gateways"
      :class="{active: value === 'visa'}"
      @click="$emit('input', 'visa')"
    >
      <img
        :src="isDark ? paymentMethods.visa.logoDark : paymentMethods.visa.logo"
        alt="visa"
        height="9.2"
      >
      <img
        :src="isDark ? paymentMethods.master.logoDark : paymentMethods.master.logo"
        alt="master"
        height="16"
      >
      <img
        :src="isDark ? paymentMethods.amex.logoDark : paymentMethods.amex.logo"
        alt="amex"
        height="12.3"
      >
    </div>
    <div
      class="payment-gateway-option"
      :class="{active: value === 'paypal'}"
      @click="$emit('input', 'paypal')"
    >
      <img
        :src="isDark ? paymentMethods.paypal.logoDark : paymentMethods.paypal.logo"
        alt="PayPal"
        height="17"
      >
    </div>

    <div
      v-if="!isRecurring && preferableMethod"
      class="payment-gateway-option"
      :class="{active: value === preferableMethod}"
      @click="$emit('input', preferableMethod)"
    >
      <img
        :src="isDark ? paymentMethods[preferableMethod].logoDark : paymentMethods[preferableMethod].logo"
        :alt="preferableMethod"
        height="22"
      >
    </div>

    <b-dropdown
      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
      toggle-class="payment-gateway-option other-methods"
      :variant="isDropdownActive ? 'primary' : 'outline-dark'"
    >
      <template #button-content>
        <div :class="{'text-primary': !isDropdownActive}">
          {{ $t('payment.other-methods') }}
        </div>
      </template>

      <b-dropdown-item-button
        v-if="isRecurring || preferableMethod !== 'knet'"
        :active="value === 'knet'"
        @click="$emit('input', 'knet')"
      >
        <img
          :src="isDark ? paymentMethods.knet.logoDark : paymentMethods.knet.logo"
          alt="knet"
          height="28"
        >
      </b-dropdown-item-button>
      <b-dropdown-item-button
        v-if="isRecurring || preferableMethod !== 'benefit'"
        :active="value === 'benefit'"
        @click="$emit('input', 'benefit')"
      >
        <img
          :src="isDark ? paymentMethods.benefit.logoDark : paymentMethods.benefit.logo"
          alt="fawry"
          height="22"
        >
      </b-dropdown-item-button>
      <b-dropdown-item-button
        :active="value === 'wiretransfer'"
        @click="$emit('input', 'wiretransfer')"
      >
        <img
          src="@/assets/images/payment/payment-methods/light/wire-transfer.png"
          alt="wire-transfer"
          height="32"
        >
      </b-dropdown-item-button>
      <b-dropdown-item-button
        v-if="!isRecurring && preferableMethod !== 'fawry'"
        :active="value === 'fawry'"
        @click="$emit('input', 'fawry')"
      >
        <img
          :src="isDark ? paymentMethods.fawry.logoDark : paymentMethods.fawry.logo"
          alt="fawry"
          height="22"
        >
      </b-dropdown-item-button>
    </b-dropdown>

  </div>
</template>

<script>
import { BDropdown, BDropdownItemButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import paymentMethodsList from '@/constants/online-payment/payment-methods-list'

export default {
  name: 'SelectPaymentGateway',
  components: {
    BDropdown,
    BDropdownItemButton,
  },
  directives: {
    Ripple,
  },
  props: {
    value: {
      type: String,
      default: 'visa',
    },
    isRecurring: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      paymentMethods: paymentMethodsList,
    }
  },
  computed: {
    ...mapGetters({
      isDark: 'appConfig/isDark',
      branch: 'branches/currentBranch',
    }),
    localPaymentMethod() {
      return this.value && !['paypal', 'visa'].includes(this.value)
    },
    isDropdownActive() {
      return this.localPaymentMethod && this.value && !['paypal', 'visa', this.isRecurring ? '' : this.preferableMethod].includes(this.value)
    },
    preferableMethod() {
      const code = (this.branch?.country_iso ?? '').toLowerCase()
      switch (code) {
        case 'kw': return 'knet'
        case 'bh': return 'benefit'
        case 'eg': return 'fawry'
        default: return ''
      }
    },
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/utils/responsive.scss";

.select-payment-gateway {
  margin-bottom: 18px;
  display: flex;
  flex-wrap: wrap;

  .payment-gateway-option {
    &:not(.other-methods) {
      margin-bottom: 10px;
      flex-grow: 1;

      @include respond-to('large') {
        max-width: 136px;
      }
    }

    height: 52px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 136px;

    border-radius: 5px;
    box-shadow: 0 3px 14px 0 rgba(0, 0, 0, 0.07);
    border: solid 1px var(--custom-control-border-color) !important;

    body.dark-layout & {
      box-shadow: unset;
      border: solid 1px var(--theme-dark-input-border-color) !important;
      &.active {
        border-color: var(--primary) !important;
      }
    }

    &.active {
      border-color: var(--primary) !important;
    }

    &.other-methods {
      padding: 0 20px;
      width: auto;
      min-width: 200px;
      display: flex;
      justify-content: space-between;

      .dropdown-toggle::after {
        left: unset!important;
        right: unset!important;

        body:not(.dark-layout) & {
          color: #6d6a7a!important;
        }
      }
    }

    &.multiple-gateways img {
      &:not(:last-child) {
        padding-right: 10px;
      }

      [dir=rtl] & {
        &:not(:last-child) {
          padding-left: 10px!important;
          padding-right: 0!important;
        }
      }
    }

    [dir=ltr] &, [dir] & {
      &:not(:last-child) {
        margin-right: 10px;
      }
    }

    [dir=rtl] & {
      &:not(:last-child) {
        margin-left: 10px;
      }
    }
  }

  .b-dropdown .dropdown-menu {
    min-width: 199px;

    button {
      width: 100%;
      display: block;
      text-align: center!important;
      padding-top: 12.5px!important;
      padding-bottom: 12.5px!important;
    }
  }
}
</style>
